import { SubTableFooter } from "@/design-system/components/ui";
import type { ListFilterDefs, ListFilters } from "@/hooks/useListFilters";

/**
 * Page controls for the Buyer CRM lists.
 *
 * Reads and writes the page through the shared filter shell, so paging lands in
 * the query string alongside the filters and a shared link points at the page
 * the sender was actually looking at.
 *
 * 🔴 THE READOUT IS THE SHARED ONE. Table and Tile Standard §10 fixes a single
 * contract — `Showing X–Y of Z · Page N of M` on the left, PREVIOUS / NEXT on
 * the right, both buttons disabled at the ends rather than hidden so the control
 * does not change size as the reader pages. This used to emit `1–2 of 200` and
 * `Page 1 of 8` as three separate nodes, which is the same two facts in a
 * different arrangement. `SubTableFooter` is exported from `SubTable` for
 * exactly this case — a table that cannot use `SubTable`'s own client-side
 * paging still gets the same footer instead of inventing a third one.
 *
 * WHY NOT `SubTable`'s BUILT-IN FOOTER. These lists page SERVER-side, 25 rows at
 * a time. `SubTable` pages the array it is handed, so letting it page would slice
 * an already-sliced page into ten and give the reader two pagers that disagree.
 * The page keeps the server pager; `SubTable` is told it will never hold enough
 * rows to earn chrome of its own.
 */
export function BuyerPager<D extends ListFilterDefs>({
  filters,
  total,
  shown,
  pageSize = 25,
  hidden = false,
}: {
  filters: ListFilters<D>;
  total: number;
  shown: number;
  pageSize?: number;
  /**
   * §5 — the footer is absent entirely below the chrome threshold. The caller
   * owns that decision because it is the one holding the unfiltered row count;
   * a caller that says nothing keeps today's behaviour.
   */
  hidden?: boolean;
}) {
  const { page, setPage } = filters;
  const lastPage = Math.max(1, Math.ceil(total / pageSize));
  if (hidden || total === 0) return null;

  const first = (page - 1) * pageSize + 1;
  const last = first + shown - 1;

  return (
    <SubTableFooter
      firstShown={first}
      lastShown={last}
      total={total}
      page={page}
      totalPages={lastPage}
      onPrevious={() => setPage(page - 1)}
      onNext={() => setPage(page + 1)}
      className="mt-space-3"
    />
  );
}
